import type { TranscriptEntry } from "../../shared/types.ts";
import {
  buildAnswerBlocks,
  type AnswerBlock,
} from "../../shared/suggestions/checkpoints.ts";

const COMPLETION_PAUSE_MS = 5_000;

export interface GenerationProgress {
  words: number;
  seconds: number;
  pendingBlocks: number;
  ready: boolean;
  stale: boolean;
}

export function computeGenerationProgress(input: {
  entries: TranscriptEntry[];
  thresholdWords: number;
  thresholdSeconds: number;
  processedThroughTs: number | null;
  checkpointThroughTs?: number | null;
  now: number;
}): GenerationProgress {
  const {
    entries,
    thresholdWords,
    thresholdSeconds,
    processedThroughTs,
    checkpointThroughTs = null,
    now,
  } = input;
  if (entries.length === 0) {
    return { words: 0, seconds: 0, pendingBlocks: 0, ready: false, stale: false };
  }
  const blocks = buildAnswerBlocks(entries, COMPLETION_PAUSE_MS, now);
  const current = blocks.at(-1);
  const uncovered = current ? progressAfterCheckpoint(current, checkpointThroughTs) : null;
  const words = uncovered?.wordCount ?? 0;
  const seconds = uncovered ? Math.round(uncovered.durationMs / 1000) : 0;
  const coverageThroughTs = checkpointThroughTs ?? processedThroughTs;
  const pendingBlocks =
    coverageThroughTs == null
      ? blocks.length
      : blocks.filter((b) => (b.entries.at(-1)?.ts ?? 0) > coverageThroughTs).length;
  const stale = current?.isStale ?? false;
  const ready = !stale && words >= thresholdWords && seconds >= thresholdSeconds;
  return { words, seconds, pendingBlocks, ready, stale };
}

function progressAfterCheckpoint(
  block: AnswerBlock,
  checkpointThroughTs: number | null,
): Pick<AnswerBlock, "durationMs" | "wordCount"> {
  const startTs = block.entries[0].ts;
  const endTs = block.endTs;
  if (
    checkpointThroughTs === null ||
    checkpointThroughTs < startTs ||
    checkpointThroughTs > endTs
  ) {
    return block;
  }

  return {
    durationMs: Math.max(0, endTs - checkpointThroughTs),
    wordCount: block.entries
      .filter((entry) => entry.ts > checkpointThroughTs)
      .map((entry) => entry.text.trim())
      .join(" ")
      .split(/\s+/)
      .filter(Boolean).length,
  };
}
